import { useState } from "react";
import { ChevronLeft, ChevronRight, Play, X } from "lucide-react";

function resolveUrl(u) {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  if (u.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${u}`;
  return u;
}

function youTubeEmbed(url) {
  if (!url) return null;
  // Accept watch?v=, youtu.be/, shorts/
  const m =
    url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([\w-]{11})/);
  return m ? `https://www.youtube.com/embed/${m[1]}?rel=0` : null;
}

/**
 * Galeria de fotos + vídeo para espaços públicos.
 * - Foto principal grande, miniaturas abaixo, clique abre lightbox.
 * - Se houver vídeo (YouTube ou MP4), aparece como primeiro thumbnail com play.
 */
export default function SpaceGallery({ photos = [], videoUrl, name, alt }) {
  const items = [];
  if (videoUrl) items.push({ kind: "video", src: videoUrl });
  photos.forEach((p) => items.push({ kind: "photo", src: p }));

  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const yt = videoUrl ? youTubeEmbed(videoUrl) : null;
  const isMp4 = videoUrl && /\.mp4(\?|$)/i.test(videoUrl);

  if (items.length === 0) return null;
  const current = items[active] || items[0];
  const total = items.length;

  return (
    <div data-testid="space-gallery">
      <div className="aspect-[16/10] bg-black/20 overflow-hidden relative">
        {current.kind === "video" ? (
          yt ? (
            <iframe
              src={yt}
              title={`Vídeo ${name || ""}`}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : isMp4 ? (
            <video src={videoUrl} controls className="w-full h-full object-cover" />
          ) : (
            <a href={videoUrl} target="_blank" rel="noreferrer" className="w-full h-full bg-black/30 flex items-center justify-center text-white">
              <Play size={48} />
            </a>
          )
        ) : (
          <img
            src={resolveUrl(current.src)}
            alt={alt || name}
            className="w-full h-full object-cover cursor-zoom-in hover:scale-105 transition duration-700"
            onClick={() => setLightbox(true)}
          />
        )}
        {total > 1 && current.kind === "photo" && (
          <>
            <button
              type="button"
              onClick={() => setActive((i) => (i - 1 + total) % total)}
              aria-label="Anterior"
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/70 text-white p-2 rounded-full"
              data-testid="gallery-prev"
            ><ChevronLeft size={18} /></button>
            <button
              type="button"
              onClick={() => setActive((i) => (i + 1) % total)}
              aria-label="Próxima"
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/70 text-white p-2 rounded-full"
              data-testid="gallery-next"
            ><ChevronRight size={18} /></button>
          </>
        )}
      </div>

      {total > 1 && (
        <div className="flex gap-2 p-3 overflow-x-auto" data-testid="gallery-thumbs">
          {items.map((it, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => setActive(idx)}
              className={`flex-shrink-0 w-16 h-12 rounded overflow-hidden border-2 ${active === idx ? "border-[#D4AF37]" : "border-transparent opacity-70 hover:opacity-100"} relative`}
              aria-label={it.kind === "video" ? "Vídeo" : `Foto ${idx + 1}`}
              data-testid={`gallery-thumb-${idx}`}
            >
              {it.kind === "video" ? (
                <div className="w-full h-full bg-black/60 flex items-center justify-center text-white">
                  <Play size={14} />
                </div>
              ) : (
                <img src={resolveUrl(it.src)} alt="" className="w-full h-full object-cover" />
              )}
            </button>
          ))}
        </div>
      )}

      {lightbox && current.kind === "photo" && (
        <div
          className="fixed inset-0 bg-black/90 z-[60] flex items-center justify-center p-4"
          onClick={() => setLightbox(false)}
          data-testid="gallery-lightbox"
        >
          <button
            type="button"
            onClick={() => setLightbox(false)}
            className="absolute top-4 right-4 text-white/80 hover:text-white"
            aria-label="Fechar"
          ><X size={28} /></button>
          <img
            src={resolveUrl(current.src)}
            alt={alt || name}
            className="max-w-[95vw] max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
